import type { JSX } from 'react';
import React from 'react';
import type { ContactFormCommunicationChannel as Channel } from 'graphql-ts-types';

import Alert from 'common/components/Alert';
import FormGroup from 'common/components/Form/FormGroup';
import { HeadlineH5 } from 'common/components/Typography';
import { MessageType } from 'common/enums/Alert';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { DispatchState } from 'common/types/React';
import { formatTime } from '../../utils/channel';
import { FieldName } from '../enums';
import { headlineStyles } from '../Headline';
import type { ContactFormGroup, Settings } from '../types';
import type { ChannelListFormData } from './ChannelList';

interface ChannelFieldsProps {
  formGroups: ContactFormGroup[];
  submitButton: JSX.Element;
  settings: Settings;
  callbackNotAvailableError: boolean;
  onSelectChannel: (c: Channel) => void;
  setCallbackNotAvailableError: DispatchState<boolean>;
}

const HEADING_ID = 'contact-form-channel-heading';

const ChannelFields = ({
  formGroups,
  submitButton,
  settings,
  callbackNotAvailableError,
  onSelectChannel,
  setCallbackNotAvailableError,
}: ChannelFieldsProps) => {
  const { t, r } = useLocalization();
  const channelFormGroup = formGroups.find(
    (group): group is ChannelListFormData => group.name === FieldName.Channel
  );
  const openingTime = formatTime(settings.channels?.openingTime ?? '');
  const closingTime = formatTime(settings.channels?.closingTime ?? '');

  return (
    <>
      <HeadlineH5 as="h1" id={HEADING_ID} {...headlineStyles}>
        {t('h24_contact_form_reason_step_headline')}
      </HeadlineH5>

      {callbackNotAvailableError && (
        <Alert mb={['sp_16', 'sp_20']} type={MessageType.Error}>
          {r.t('h24_channel_option_callback_not_available_error', {
            openingTime,
            closingTime,
          })}
        </Alert>
      )}
      {channelFormGroup && (
        <FormGroup
          key={channelFormGroup.name}
          ariaLabelledBy={HEADING_ID}
          {...{
            ...channelFormGroup,
            onSelectChannel,
            setCallbackNotAvailableError,
          }}
        />
      )}
      {submitButton}
    </>
  );
};

export default ChannelFields;
